import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { Card, CardContent, Typography, Grid, Button, Switch, FormControlLabel, TextField, CircularProgress, Snackbar, SnackbarContent } from '@material-ui/core'
import { useTourPricing } from '../hooks/useTourPricing'
import { useTourValidation } from '../hooks/useTourValidation'
import { TourContext } from '../contexts/TourContext'
import { tourApi } from '../api/tourApi'

/**
 * Optimized tour processing component
 * Implements improvements identified in comparative analysis
 */
const TourProcessingOptimized = React.memo(({ tourId, onSuccess, onError }) => {
  const { tourState, setTourState } = React.useContext(TourContext)
  
  // Custom hooks for calculations and validations
  const { pricing, loading: pricingLoading, error: pricingError } = useTourPricing(tourId)
  const { validation, loading: validationLoading, error: validationError } = useTourValidation(tourId)
  
  // Optimized local state
  const [formData, setFormData] = useState({
    vehicle_id: '',
    gpsdevice_id: '',
    r_gps: false,
    r_insurance: false,
    r_cancel_insurance: false,
    r_single: false,
    low_seat: false,
    discount: 0,
    rewards: 0,
    value_to_charge: 0,
    comments: '',
    imt_comment: ''
  })
  
  const [processing, setProcessing] = useState(false)
  const [notifications, setNotifications] = useState([])

  // Memoization of expensive calculations
  const calculatedPricing = useMemo(() => {
    if (!pricing) return null
    
    return {
      total: pricing.total,
      deposit: pricing.deposit,
      breakdown: pricing.breakdown,
      rules: pricing.rules
    }
  }, [pricing])

  // Memoization of validations
  const validationSummary = useMemo(() => {
    if (!validation) return null
    
    return {
      isValid: validation.isValid,
      errors: validation.errors,
      warnings: validation.warnings
    }
  }, [validation])

  // Optimized callback for form changes
  const handleFormChange = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
    
    // Update global context
    setTourState(prev => ({
      ...prev,
      formData: { ...formData, [field]: value }
    }))
  }, [formData, setTourState])

  // Optimized callback for processing
  const handleProcessTour = useCallback(async () => {
    if (!(validationSummary && validationSummary.isValid)) {
      setNotifications(prev => [...prev, {
        type: 'error',
        message: 'Please correct errors before processing'
      }])
      return
    }

    setProcessing(true)
    
    try {
      const result = await tourApi.processTour(tourId, formData)
      
      if (result.success) {
        setNotifications(prev => [...prev, {
          type: 'success',
          message: 'Tour processed successfully'
        }])
        if (onSuccess) onSuccess(result)
      } else {
        setNotifications(prev => [...prev, {
          type: 'error',
          message: result.error || 'Error processing tour'
        }])
        if (onError) onError(result.error)
      }
    } catch (error) {
      setNotifications(prev => [...prev, {
        type: 'error',
        message: 'Network error occurred'
      }])
      if (onError) onError(error)
    } finally {
      setProcessing(false)
    }
  }, [tourId, formData, validationSummary, onSuccess, onError])

  // Skeleton loading while data is loading
  if (pricingLoading || validationLoading) {
    return <TourSkeleton />
  }

  // Error handling
  if (pricingError || validationError) {
    return (
      <Snackbar open={true}>
        <SnackbarContent
          style={{ backgroundColor: '#f44336', color: 'white' }}
          message={`Error loading tour data: ${pricingError || validationError}`}
        />
      </Snackbar>
    )
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          Tour Processing
        </Typography>

        {/* Notifications */}
        {notifications.map((notification, index) => (
          <Snackbar 
            key={index}
            open={true}
            onClose={() => setNotifications(prev => prev.filter((_, i) => i !== index))}
            style={{ marginBottom: 16 }}
          >
            <SnackbarContent
              style={{ 
                backgroundColor: notification.type === 'success' ? '#4caf50' : '#f44336', 
                color: 'white' 
              }}
              message={notification.message}
            />
          </Snackbar>
        ))}

        <Grid container spacing={24}>
          {/* Prices Panel */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Price Calculation
                </Typography>
                
                {calculatedPricing && (
                  <div>
                    <Typography variant="body1">
                      <strong>Total:</strong> €{calculatedPricing.total}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Deposit:</strong> €{calculatedPricing.deposit}
                    </Typography>
                    
                    {/* Price breakdown */}
                    <Typography variant="subtitle2" style={{ marginTop: 16 }}>
                      Breakdown:
                    </Typography>
                    {Object.entries(calculatedPricing.breakdown).map(([key, value]) => (
                      <Typography key={key} variant="body2">
                        {value.description}: €{value.price}
                      </Typography>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Validation Panel */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Validation
                </Typography>
                
                {validationSummary && (
                  <div>
                    {validationSummary.isValid ? (
                      <Snackbar open={true}>
                        <SnackbarContent
                          style={{ backgroundColor: '#4caf50', color: 'white' }}
                          message="Valid data for processing"
                        />
                      </Snackbar>
                    ) : (
                      <Snackbar open={true}>
                        <SnackbarContent
                          style={{ backgroundColor: '#f44336', color: 'white' }}
                          message={
                            <div>
                              <strong>Errors found:</strong>
                              {validationSummary.errors.map((error, index) => (
                                <div key={index}>• {error}</div>
                              ))}
                            </div>
                          }
                        />
                      </Snackbar>
                    )}
                    
                    {validationSummary.warnings.length > 0 && (
                      <Snackbar open={true} style={{ marginTop: 8 }}>
                        <SnackbarContent
                          style={{ backgroundColor: '#ff9800', color: 'white' }}
                          message={
                            <div>
                              <strong>Warnings:</strong>
                              {validationSummary.warnings.map((warning, index) => (
                                <div key={index}>• {warning}</div>
                              ))}
                            </div>
                          }
                        />
                      </Snackbar>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Options Form */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Tour Options
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.r_gps}
                          onChange={(e) => handleFormChange('r_gps', e.target.checked)}
                        />
                      }
                      label="GPS"
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.r_insurance}
                          onChange={(e) => handleFormChange('r_insurance', e.target.checked)}
                        />
                      }
                      label="VIP Insurance"
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.r_cancel_insurance}
                          onChange={(e) => handleFormChange('r_cancel_insurance', e.target.checked)}
                        />
                      }
                      label="Cancellation Insurance"
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.r_single}
                          onChange={(e) => handleFormChange('r_single', e.target.checked)}
                        />
                      }
                      label="Single Room"
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Discount (€)"
                      type="number"
                      value={formData.discount}
                      onChange={(e) => handleFormChange('discount', parseFloat(e.target.value) || 0)}
                      inputProps={{ min: 0, max: 1000 }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Rewards (€)"
                      type="number"
                      value={formData.rewards}
                      onChange={(e) => handleFormChange('rewards', parseFloat(e.target.value) || 0)}
                      inputProps={{ min: 0, max: 1000 }}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Comments"
                      multiline
                      rows={3}
                      value={formData.comments}
                      onChange={(e) => handleFormChange('comments', e.target.value)}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="IMT Comments"
                      multiline
                      rows={3}
                      value={formData.imt_comment}
                      onChange={(e) => handleFormChange('imt_comment', e.target.value)}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Processing Button */}
          <Grid item xs={12}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              fullWidth
                             disabled={processing || !(validationSummary && validationSummary.isValid)}
              onClick={handleProcessTour}
              startIcon={processing ? <CircularProgress size={20} /> : null}
            >
              {processing ? 'Processing...' : 'Process Tour'}
            </Button>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
})

/**
 * Loading component for progressive loading
 */
const TourSkeleton = () => (
  <Card>
    <CardContent style={{ textAlign: 'center', padding: 40 }}>
      <CircularProgress size={60} />
      <Typography variant="h6" style={{ marginTop: 16 }}>
        Loading tour data...
      </Typography>
    </CardContent>
  </Card>
)

export default TourProcessingOptimized 


// WEBPACK FOOTER //
// ./src/features/tours/components/TourProcessingOptimized.js